import type { Block, Thought } from './types'

const WORDS_PER_MINUTE = 220

const ENTITIES: Record<string, string> = { '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" }

/** Plain text of a stored inline-HTML string (for counting, snippets, copies). */
export function stripTags(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m] ?? m)
}

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export function countWordsIn(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

export function blockText(block: Block): string {
  switch (block.type) {
    case 'text':
    case 'heading':
    case 'pullquote':
    case 'scene':
      return stripTags(block.html)
    case 'thought':
    case 'margin':
    case 'unfold':
      return block.text
    case 'image':
      return block.caption
    case 'linkcard':
      return [block.title, block.description].filter(Boolean).join(' ')
    case 'voice':
      return block.transcript
    case 'divider':
    case 'columns':
    case 'collection':
      return ''
    default:
      // An old pin (it carries its words as text), or a block type this build doesn't
      // know (written by a newer one): no words rather than a crash.
      return (block as { type: string }).type === 'pinned' ? String((block as { text?: unknown }).text ?? '') : ''
  }
}

/** Plain text normalised the way save_writing_line() compares excerpts. */
export function normalizeSpace(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Words a reader reads in the column. Margin asides (notes, pins, voice
 * transcripts) and tucked unfolds are extras, so they don't lengthen the
 * "6 min read".
 */
export function countWords(title: string, blocks: Block[]): number {
  return blocks.reduce(
    (n, b) =>
      b.type === 'margin' || (b as { type: string }).type === 'pinned' || b.type === 'voice' || b.type === 'unfold'
        ? n
        : n + countWordsIn(blockText(b)),
    countWordsIn(title),
  )
}

export function readMinutes(words: number): number {
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE))
}

export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

export function slugify(input: string, max = 80): string {
  const base = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (base.length <= max) return base
  const cut = base.slice(0, max)
  const lastDash = cut.lastIndexOf('-')
  return (lastDash > max / 3 ? cut.slice(0, lastDash) : cut).replace(/-+$/, '')
}

export function hostOf(url?: string | null): string {
  if (!url) return ''
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

/** "8:14 am" */
export function clockTime(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(/\s?AM$/, ' am')
    .replace(/\s?PM$/, ' pm')
}

/** "Aug 30" */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** "Sep 8, 2026" */
export function longDate(iso: string | Date): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** "Tuesday, September 9" */
export function weekdayDate(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

/** "Tue, Sep 9" */
export function shortWeekdayDate(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/** Local calendar date as YYYY-MM-DD (for a date column). */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Local midnight `daysAgo` calendar days before `now`. */
export function startOfDay(daysAgo: number, now = new Date()): Date {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - daysAgo)
  return d
}

/** How many calendar days before `now` the timestamp falls (0 = today). */
export function calendarDaysAgo(iso: string, now = new Date()): number {
  const then = new Date(iso)
  then.setHours(0, 0, 0, 0)
  return Math.round((startOfDay(0, now).getTime() - then.getTime()) / 86_400_000)
}

export function dayName(daysAgo: number, now = new Date()): string {
  if (daysAgo === 0) return 'Today'
  if (daysAgo === 1) return 'Yesterday'
  return shortWeekdayDate(startOfDay(daysAgo, now))
}

/** "2h ago", "3d ago", or a date once it's old. */
export function timeAgo(iso: string, now = new Date()): string {
  const s = Math.max(0, (now.getTime() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86_400) return `${Math.floor(s / 3600)}h ago`
  if (s < 7 * 86_400) return `${Math.floor(s / 86_400)}d ago`
  return shortDate(iso)
}

/** "Saved from substack.com", "Photo", "Note". */
export function thoughtKind(t: Pick<Thought, 'image_path' | 'source_url'>): string {
  if (t.image_path) return 'Photo'
  const host = hostOf(t.source_url)
  if (host) return `Saved from ${host}`
  return 'Note'
}

/** Clock time for today's thoughts, a date for older ones. */
export function thoughtStamp(iso: string, now = new Date()): string {
  return calendarDaysAgo(iso, now) === 0 ? clockTime(iso) : shortDate(iso)
}

export function displayName(p: { display_name: string | null; handle: string | null } | null | undefined): string {
  return p?.display_name || p?.handle || 'You'
}
